import { useCallback, useEffect, useState } from "react";
import {
  adminListTenants,
  adminCreateTenant,
  adminDeleteTenant,
  updateTenantModule,
  adminGetTenantChannels,
  adminSetTenantChannel,
  adminSetTenantChannelCredentials,
} from "../api/client";
import type { AdminTenant, TenantChannelsStatus, TenantChannelUpdate } from "../api/types";
import { useToast } from "../context/ToastContext";

export function AdminTenants() {
  const [tenants, setTenants] = useState<AdminTenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await adminListTenants();
      setTenants(data);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (tenantId: string) => {
    if (!confirm(`Eliminare il tenant "${tenantId}"? Azione irreversibile.`)) return;
    try {
      await adminDeleteTenant(tenantId);
      toast.addToast("success", `Tenant "${tenantId}" eliminato`);
      load();
    } catch (err) {
      toast.addToast("error", (err as Error).message);
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">
          <span className="material-symbols-outlined icon-xl" style={{ marginRight: 8 }}>apartment</span>
          Gestione Clienti
        </h1>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>add</span>
          Nuovo cliente
        </button>
      </div>

      {error && (
        <div className="card" style={{ borderLeft: "4px solid var(--indaco-danger)", marginBottom: "var(--space-4)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--indaco-danger)" }}>
            <span className="material-symbols-outlined">error</span>
            {error}
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: "center", padding: "var(--space-8)" }}>
          <span className="spinner spinner-lg" />
        </div>
      ) : tenants.length === 0 ? (
        <div className="card" style={{ textAlign: "center", color: "var(--indaco-gray-500)" }}>
          Nessun tenant configurato
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          <div className="table-container">
            <table style={{ tableLayout: "fixed", width: "100%", minWidth: 950 }}>
              <thead>
                <tr>
                  <th style={{ width: 160 }}>ID</th>
                  <th style={{ width: 200 }}>Nome</th>
                  <th style={{ width: 220 }}>Flussi attivi</th>
                  <th style={{ width: 160 }}>Agente</th>
                  <th style={{ width: 120 }}>Creato il</th>
                  <th style={{ width: 90 }}>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map((t) => (
                  <tr key={t.tenant_id}>
                    <td style={{ overflow: "hidden", textOverflow: "ellipsis" }}><code>{t.tenant_id}</code></td>
                    <td style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{t.name}</td>
                    <td><FlowsCell tenant={t} /></td>
                    <td><AgentStatusCell tenant={t} onChanged={load} /></td>
                    <td>{formatDate(t.created_at)}</td>
                    <td>
                      <button
                        className="btn btn-ghost btn-sm"
                        style={{ color: "var(--indaco-danger)" }}
                        onClick={() => handleDelete(t.tenant_id)}
                        title="Elimina tenant"
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: 18 }}>delete</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showCreate && (
        <CreateTenantDialog
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
    </div>
  );
}

/* ── Flussi attivi per cliente ──
 * Il superadmin governa qui quali clienti hanno un modulo, senza dover
 * entrare nel contesto di ciascun tenant. Sono badge di sola accensione
 * (nessuna configurazione di dettaglio esiste ancora per questi flussi dal
 * lato superadmin cross-tenant) — l'Agente ha una colonna a sé, vedi
 * `AgentStatusCell`, perché il suo stato reale non è un semplice on/off. */
function FlowsCell({ tenant }: { tenant: AdminTenant }) {
  const otherModules = tenant.modules.filter((m) => m !== "conversazioni");
  if (otherModules.length === 0) {
    return <span className="text-xs text-muted">nessuno</span>;
  }
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-2)" }}>
      {otherModules.map((m) => (
        <span key={m} className="badge badge-neutral" style={{ fontSize: "var(--font-size-xs)" }}>{m}</span>
      ))}
    </div>
  );
}

/* ── Stato agente per cliente ──
 * "Agente: ON" rifletteva solo il flag di modulo, non se un canale fosse
 * davvero configurato — un cliente poteva vedersi "ON" senza essere
 * raggiungibile da nessuna parte. Qui si legge lo stato REALE dal canale
 * (oggi solo WhatsApp esiste nel modello dati — niente "Web" finché non è
 * implementato per davvero): modulo spento -> "Non attivo" (grigio);
 * modulo acceso ma nessun canale abilitato -> avviso esplicito, mai un
 * colore muto a fare da solo segnale; modulo acceso e canale abilitato ->
 * verde con il nome del canale. Il badge resta cliccabile in ogni stato,
 * apre lo stesso `AgentChannelsDialog` di prima (accensione + credenziali). */
function AgentStatusCell({ tenant, onChanged }: { tenant: AdminTenant; onChanged: () => void }) {
  const [showAgent, setShowAgent] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const hasConversazioni = tenant.modules.includes("conversazioni");
  const [channels, setChannels] = useState<TenantChannelsStatus["channels"] | null>(null);
  const [loading, setLoading] = useState(hasConversazioni);

  useEffect(() => {
    if (!hasConversazioni) {
      setChannels(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    adminGetTenantChannels(tenant.tenant_id)
      .then((data) => { if (!cancelled) setChannels(data.channels); })
      .catch(() => { if (!cancelled) setChannels(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [tenant.tenant_id, hasConversazioni, refreshKey]);

  const activeChannels = channels ? Object.entries(channels).filter(([, s]) => s.enabled) : [];

  let badgeClass = "badge-neutral";
  let label = "Non attivo";
  if (hasConversazioni) {
    if (loading) {
      label = "…";
    } else if (activeChannels.length > 0) {
      badgeClass = "badge-success";
      label = activeChannels.map(([c]) => c).join(", ");
    } else {
      badgeClass = "badge-warning";
      label = "Acceso, nessun canale";
    }
  }

  return (
    <>
      <button
        type="button"
        className={`badge ${badgeClass}`}
        style={{ fontSize: "var(--font-size-xs)", cursor: "pointer", border: "none", textTransform: "capitalize" }}
        onClick={() => setShowAgent(true)}
        title="Vedi e gestisci l'agente conversazionale per questo cliente"
      >
        {label}
      </button>
      {showAgent && (
        <AgentChannelsDialog
          tenant={tenant}
          onClose={() => { setShowAgent(false); setRefreshKey((k) => k + 1); }}
          onChanged={onChanged}
        />
      )}
    </>
  );
}

// Campi credenziale per canale — solo WhatsApp oggi (vedi
// `WhatsAppChannelConfig` in src/models/tenant.py, sola lettura). La GET
// `.../channels` non restituisce MAI questi valori (nemmeno lo stato
// "configurato": vedi `_safe_channels_view` in admin_tenants.py, che
// espone solo `enabled`), quindi qui non c'e' alcun modo di sapere se un
// campo e' gia' valorizzato lato server prima di questa sessione — lo stato
// "configurato" mostrato sotto riflette solo i salvataggi fatti in QUESTA
// sessione di dialog, mai un valore letto da GET/cache/localStorage.
const CHANNEL_CREDENTIAL_FIELDS: Record<string, { field: keyof TenantChannelUpdate; label: string }[]> = {
  whatsapp: [
    { field: "access_token", label: "Access Token" },
    { field: "phone_number_id", label: "Phone Number ID" },
    { field: "webhook_verify_token", label: "Webhook Verify Token" },
    { field: "business_account_id", label: "Business Account ID" },
  ],
};

/* ── Vista "Agente" ──
 * Modale minimale locale a questo file (segue lo stesso pattern
 * dialog-overlay/dialog-card di `CreateTenantDialog` sotto): accensione del
 * modulo `conversazioni` (stesso PATCH di sempre, nessuna regressione) più
 * stato per-canale, letto da `GET /admin/tenants/{id}/channels` — un
 * endpoint che restituisce SOLO flag `enabled`, mai `access_token` o
 * `phone_number_id` — più un form per impostare/sostituire le credenziali
 * di canale (write-only: nessuna precompilazione, nessun valore mai
 * mostrato, solo lo stato "configurato" aggiornato dopo un salvataggio
 * riuscito in questa sessione). */
function AgentChannelsDialog({
  tenant,
  onClose,
  onChanged,
}: {
  tenant: AdminTenant;
  onClose: () => void;
  onChanged: () => void;
}) {
  const toast = useToast();
  const hasConversazioni = tenant.modules.includes("conversazioni");
  const [moduleSaving, setModuleSaving] = useState(false);
  const [channels, setChannels] = useState<TenantChannelsStatus["channels"] | null>(null);
  const [loadingChannels, setLoadingChannels] = useState(true);
  const [channelError, setChannelError] = useState("");
  const [channelSaving, setChannelSaving] = useState<string | null>(null);
  // Valori digitati dall'utente, per canale/campo — mai precompilati, mai
  // persistiti oltre questa sessione di dialog (svuotati subito dopo un
  // salvataggio riuscito, cosi' il segreto appena scritto non resta a
  // galleggiare in stato React piu' del necessario).
  const [credentialInputs, setCredentialInputs] = useState<Record<string, Record<string, string>>>({});
  // "configurato"/"non configurato" per canale/campo, noto SOLO per i campi
  // salvati con successo in questa sessione — vedi nota sopra
  // `CHANNEL_CREDENTIAL_FIELDS`: la GET non da' altro modo di saperlo.
  const [credentialConfigured, setCredentialConfigured] = useState<Record<string, Record<string, boolean>>>({});
  const [credentialSaving, setCredentialSaving] = useState<string | null>(null);

  const loadChannels = useCallback(async () => {
    setLoadingChannels(true);
    setChannelError("");
    try {
      const data = await adminGetTenantChannels(tenant.tenant_id);
      setChannels(data.channels);
    } catch (err) {
      setChannelError((err as Error).message);
    } finally {
      setLoadingChannels(false);
    }
  }, [tenant.tenant_id]);

  useEffect(() => { loadChannels(); }, [loadChannels]);

  const handleToggleModule = async () => {
    setModuleSaving(true);
    try {
      await updateTenantModule(tenant.tenant_id, "conversazioni", !hasConversazioni);
      toast.addToast(
        "success",
        `Agente conversazionale ${!hasConversazioni ? "attivato" : "disattivato"} per "${tenant.tenant_id}"`
      );
      onChanged();
    } catch (err) {
      toast.addToast("error", (err as Error).message);
    } finally {
      setModuleSaving(false);
    }
  };

  const handleToggleChannel = async (channel: string, enabled: boolean) => {
    setChannelSaving(channel);
    try {
      const data = await adminSetTenantChannel(tenant.tenant_id, channel, enabled);
      setChannels(data.channels);
      toast.addToast(
        "success",
        `Canale ${channel} ${enabled ? "attivato" : "disattivato"} per "${tenant.tenant_id}"`
      );
    } catch (err) {
      toast.addToast("error", (err as Error).message);
    } finally {
      setChannelSaving(null);
    }
  };

  const handleCredentialInputChange = (channel: string, field: string, value: string) => {
    setCredentialInputs((prev) => ({
      ...prev,
      [channel]: { ...(prev[channel] || {}), [field]: value },
    }));
  };

  const handleSaveCredentials = async (channel: string) => {
    const fields = CHANNEL_CREDENTIAL_FIELDS[channel] || [];
    const inputs = credentialInputs[channel] || {};
    const update: TenantChannelUpdate = {};
    const changedFields: string[] = [];
    for (const { field } of fields) {
      const value = inputs[field];
      if (value) {
        (update as Record<string, string>)[field] = value;
        changedFields.push(field);
      }
    }
    if (changedFields.length === 0) {
      toast.addToast("error", "Inserisci almeno un campo da salvare");
      return;
    }

    setCredentialSaving(channel);
    try {
      const data = await adminSetTenantChannelCredentials(tenant.tenant_id, channel, update);
      setChannels(data.channels);
      setCredentialConfigured((prev) => ({
        ...prev,
        [channel]: {
          ...(prev[channel] || {}),
          ...Object.fromEntries(changedFields.map((f) => [f, true])),
        },
      }));
      // Il segreto appena digitato non serve piu' una volta salvato: fuori
      // dallo stato subito, cosi' non resta in memoria del componente.
      setCredentialInputs((prev) => ({ ...prev, [channel]: {} }));
      toast.addToast("success", `Credenziali ${channel} aggiornate per "${tenant.tenant_id}"`);
    } catch (err) {
      toast.addToast("error", (err as Error).message);
    } finally {
      setCredentialSaving(null);
    }
  };

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog-card" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>Agente conversazionale — {tenant.name}</h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "var(--space-5)",
          }}
        >
          <span className="text-sm">Modulo agente conversazionale</span>
          <button
            className={`btn-toggle-sm ${hasConversazioni ? "btn-outline active" : "btn-outline"}`}
            onClick={handleToggleModule}
            disabled={moduleSaving}
            title="Attiva o disattiva l'agente conversazionale per questo cliente"
          >
            {moduleSaving ? <span className="spinner spinner-sm" /> : `Agente: ${hasConversazioni ? "ON" : "OFF"}`}
          </button>
        </div>

        <h3 className="text-sm" style={{ marginBottom: "var(--space-2)", fontWeight: "var(--font-weight-semibold)" }}>
          Canali
        </h3>
        {loadingChannels ? (
          <div style={{ textAlign: "center", padding: "var(--space-4)" }}>
            <span className="spinner spinner-sm" />
          </div>
        ) : channelError ? (
          <div style={{ color: "var(--indaco-danger)", fontSize: "var(--font-size-sm)" }}>{channelError}</div>
        ) : (
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
            {channels && Object.entries(channels).map(([channel, status]) => (
              <li key={channel}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ textTransform: "capitalize" }}>{channel}</span>
                  <button
                    className={`btn-toggle-sm ${status.enabled ? "btn-outline active" : "btn-outline"}`}
                    disabled={channelSaving === channel || !hasConversazioni}
                    onClick={() => handleToggleChannel(channel, !status.enabled)}
                    title={
                      hasConversazioni
                        ? "Attiva o disattiva questo canale"
                        : "Attiva prima il modulo agente conversazionale"
                    }
                  >
                    {channelSaving === channel ? <span className="spinner spinner-sm" /> : status.enabled ? "ON" : "OFF"}
                  </button>
                </div>

                {CHANNEL_CREDENTIAL_FIELDS[channel] && (
                  <div
                    style={{
                      marginTop: "var(--space-2)",
                      paddingTop: "var(--space-2)",
                      paddingLeft: "var(--space-2)",
                      borderLeft: "2px solid var(--indaco-gray-200)",
                    }}
                  >
                    {CHANNEL_CREDENTIAL_FIELDS[channel].map(({ field, label }) => (
                      <div className="form-group" key={field} style={{ marginBottom: "var(--space-2)" }}>
                        <label
                          className="form-label"
                          style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
                        >
                          <span>{label}</span>
                          <span
                            className={`badge ${credentialConfigured[channel]?.[field] ? "badge-success" : "badge-neutral"}`}
                            style={{ fontSize: "var(--font-size-xs)" }}
                          >
                            {credentialConfigured[channel]?.[field] ? "configurato" : "non configurato"}
                          </span>
                        </label>
                        <input
                          type="password"
                          className="form-input"
                          autoComplete="new-password"
                          placeholder="Lascia vuoto per non modificare"
                          value={credentialInputs[channel]?.[field] || ""}
                          onChange={(e) => handleCredentialInputChange(channel, field, e.target.value)}
                        />
                      </div>
                    ))}
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      disabled={credentialSaving === channel}
                      onClick={() => handleSaveCredentials(channel)}
                    >
                      {credentialSaving === channel ? <span className="spinner spinner-sm" /> : "Salva credenziali"}
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}

        <div className="dialog-footer">
          <button className="btn btn-ghost" onClick={onClose}>Chiudi</button>
        </div>
      </div>
    </div>
  );
}

/* ── Create dialog ── */

function CreateTenantDialog({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [tenantId, setTenantId] = useState("");
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const toast = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await adminCreateTenant({ tenant_id: tenantId.trim(), name: name.trim() });
      toast.addToast("success", `Tenant "${tenantId}" creato`);
      onCreated();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog-card" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>Nuovo cliente</h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">ID Tenant</label>
            <input
              className="form-input"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value.replace(/[^a-z0-9_-]/g, ""))}
              placeholder="es. casadei"
              required
              autoFocus
            />
            <small className="form-hint">Solo lettere minuscole, numeri, trattini.</small>
          </div>
          <div className="form-group">
            <label className="form-label">Nome</label>
            <input
              className="form-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="es. Casadei & Pellizzaro"
              required
            />
          </div>
          {error && (
            <div style={{ color: "var(--indaco-danger)", fontSize: "var(--font-size-sm)", marginBottom: "0.75rem" }}>
              {error}
            </div>
          )}
          <div className="dialog-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Annulla</button>
            <button type="submit" className="btn btn-primary" disabled={saving || !tenantId || !name}>
              {saving ? <><span className="spinner" style={{ marginRight: 6 }} />Creazione…</> : "Crea"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return iso;
  }
}
